import React from 'react';
import Script from 'next/script';
import classes from './HowItWorks.module.scss';
import Txt from 'components/atoms/Txt/Txt';
import A from 'components/atoms/A/A';
import Image from 'components/atoms/Image/Image';
import useLocalisedImage from 'hooks/useLocalisedImage';
import { SURVEY_TIPS, CASHBACK, GAMING } from 'components/pages/pages';
import howItWorksImg1 from './assets/how_it_works_img_1.png';
import howItWorksImg2 from './assets/how_it_works_img_2.png';
import howItWorksImg2_en_gb from './assets/how_it_works_img_2_en-gb.png';
import howItWorksImg2_de_de from './assets/how_it_works_img_2_de-de.png';
import howItWorksImg2_fr_fr from './assets/how_it_works_img_2_fr-fr.png';
import howItWorksImg3 from './assets/how_it_works_img_3.png';
import howItWorksImg3_en_gb from './assets/how_it_works_img_3_en-gb.png';
import howItWorksImg3_de_de from './assets/how_it_works_img_3_de-de.png';
import howItWorksImg3_fr_fr from './assets/how_it_works_img_3_fr-fr.png';
import howItWorksImg4 from './assets/how_it_works_img_4.png';
import howItWorksImg5 from './assets/how_it_works_img_5.png';
import howItWorksImg5_en_gb from './assets/how_it_works_img_5_en-gb.png';
import howItWorksImg5_de_de from './assets/how_it_works_img_5_de-de.png';
import howItWorksImg5_fr_fr from './assets/how_it_works_img_5_fr-fr.png';
import SplitRow from 'components/organisms/SplitRow/SplitRow';
import FullWidthCta from 'components/organisms/FullWidthCta/FullWidthCta';
import TrustpilotWidget from 'components/organisms/TrustpilotWidget/TrustpilotWidget';

export interface Props {}

const Homepage: React.FC<Props> = () => {
  const localisedImage = useLocalisedImage();

  return (
    <main className="fadedPageBg">
      <Script
        type="text/javascript"
        src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
      />

      <SplitRow
        mobileColumnOrderSwapped
        leftColumn={
          <>
            <Txt component="h1">How do paid surveys work?</Txt>
            <Txt component="h2">What is a paid survey site?</Txt>
            <Txt component="p">
              Paid surveys are a fast and convenient side hustle for students, parents, or anyone
              who wants to add a little extra money into their account!
            </Txt>
            <Txt component="p">
              To get started, you just need to answer a few simple questions about yourself -
              you&apos;ll then start being matched with paid surveys that are right for you. After
              completing surveys, you&apos;ll receive cash rewards for the time spent sharing your
              opinions!
            </Txt>
            <Txt component="p">
              It&apos;s super easy to get started - you&apos;ll be getting your voice heard (whilst
              topping up your piggy bank!) in no time.
            </Txt>
          </>
        }
        rightColumn={<Image src={howItWorksImg1} alt="What is a paid survey site?" />}
      />

      <SplitRow
        rightColumn={
          <>
            <Txt component="h2">How to make money online with paid surveys?</Txt>
            <Txt component="p">
              Qmee is a free browser site and mobile app where you can earn real money for taking
              paid surveys. With no investment beforehand, you just need to tell us a bit about
              yourself to start being matched with surveys on Qmee.
            </Txt>
            <Txt component="p">
              With the Qmee mobile app, you can take surveys for cash anytime, anywhere - earn cash
              online from your phone, or make money whilst relaxing at home! Share your opinions
              with fun and engaging online surveys, and you&apos;ll get a cash reward for each one
              you complete.
            </Txt>
          </>
        }
        leftColumn={
          <Image
            src={localisedImage({
              global: howItWorksImg2,
              'en-gb': howItWorksImg2_en_gb,
              'de-de': howItWorksImg2_de_de,
              'fr-fr': howItWorksImg2_fr_fr,
            })}
            alt="How to make money online with paid surveys?"
          />
        }
      />

      <SplitRow
        mobileColumnOrderSwapped
        leftColumn={
          <>
            <Txt component="h2">Does Qmee have a minimum withdrawal amount?</Txt>
            <Txt component="p">
              The simple answer? No! There&apos;s no minimum withdrawal amount with Qmee.
            </Txt>
            <Txt component="p">
              When you complete surveys, Qmee adds your rewards to your piggy bank in real time. One
              of our top rated features is that you can withdraw your earnings at any time - meaning
              you&apos;re always in control of your rewards!
            </Txt>
            <Txt component="p">
              A no minimum cashout amount isn&apos;t something that all paid survey sites have,
              making Qmee one of the best ways to make money online!
            </Txt>
          </>
        }
        rightColumn={
          <Image
            src={localisedImage({
              global: howItWorksImg3,
              'en-gb': howItWorksImg3_en_gb,
              'de-de': howItWorksImg3_de_de,
              'fr-fr': howItWorksImg3_fr_fr,
            })}
            alt="Does Qmee have a minimum withdrawal amount?"
          />
        }
      />

      <SplitRow
        rightColumn={
          <>
            <Txt component="h2">Is Qmee a legit way to make money online?</Txt>
            <Txt component="p">
              Sharing your opinion and influencing your favorite brands, by taking paid surveys with
              Qmee, is one of the best legitimate ways to make money online in 2023. But, you
              don&apos;t have to take our word for it! Read some of our user stories to see what
              other people think. We&apos;re also rated highly on review sites.
            </Txt>

            <TrustpilotWidget showSmallWidget />
          </>
        }
        leftColumn={<Image src={howItWorksImg4} alt="Is Qmee a legit way to make money online?" />}
      />

      <SplitRow
        mobileColumnOrderSwapped
        leftColumn={
          <>
            <Txt component="h2">How can I earn cash without taking paid surveys?</Txt>
            <Txt component="p">
              Qmee not only has surveys - earn cash and free gift cards online with our{' '}
              <A to={CASHBACK.path}>cashback offers!</A> Our hand-picked money earners help you earn
              real cash from home by purchasing things you were going to buy anyway. It&apos;s a
              win-win situation!
            </Txt>
            <Txt component="p">
              With Qmee, you can also make money by <A to={GAMING.path}>playing games online</A>.
              It&apos;s a super easy and fast way to earn money from your phone without paying
              anything - just download the game and get paid to play!
            </Txt>
            <Txt component="p">
              Earn extra cash when searching online too! Add the Qmee app to your desktop browser to
              start earning money online when you search for your favorite sites and engage with the
              hottest brands!
            </Txt>
            <Txt component="p">
              With no minimum cashout amount, you can start making quick, easy money by signing up
              today!
            </Txt>
          </>
        }
        rightColumn={
          <Image
            src={localisedImage({
              global: howItWorksImg5,
              'en-gb': howItWorksImg5_en_gb,
              'de-de': howItWorksImg5_de_de,
              'fr-fr': howItWorksImg5_fr_fr,
            })}
            alt="How can I earn cash without taking paid surveys?"
          />
        }
      />

      <FullWidthCta title="Survey Tips" ctaText="View Tips" ctaLink={SURVEY_TIPS.path}>
        Check out some of the handy tips that can help with your survey experience.
      </FullWidthCta>
    </main>
  );
};

export default Homepage;
